import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";
import { Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import {
  BookOpen, Cable, FileBarChart, LayoutDashboard, Map as MapIcon, Palette, Receipt,
  ReceiptText, Route as RotaIcon, Settings, ShieldCheck, Siren, Terminal,
  ScrollText, TrendingUp, UserCog,
} from "lucide-react";
import Login from "./pages/Login";
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Mapa = lazy(() => import("./pages/Mapa"));
const Conexao = lazy(() => import("./pages/Conexao"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Percurso = lazy(() => import("./pages/Percurso"));
const Cadastros = lazy(() => import("./pages/Cadastros"));
const Conta = lazy(() => import("./pages/Conta"));
const Personalizar = lazy(() => import("./pages/Personalizar"));
const Auditoria = lazy(() => import("./pages/Auditoria"));
const Logs = lazy(() => import("./pages/Logs"));
const Espaco = lazy(() => import("./pages/Espaco"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Configurar = lazy(() => import("./pages/Configurar"));
const Faturamento = lazy(() => import("./pages/Faturamento"));
const MinhasFaturas = lazy(() => import("./pages/MinhasFaturas"));
const Recuperar = lazy(() => import("./pages/Recuperar"));
const Registrar = lazy(() => import("./pages/Registrar"));
const Manual = lazy(() => import("./pages/Manual"));
const Eventos = lazy(() => import("./pages/Eventos"));
import { Shell, Titulo, type SecaoNav } from "./components/Shell";
import { Avisos, type Aviso } from "./components/Avisos";
import { QuotaBanner } from "./components/QuotaBanner";
import { Spinner } from "./components/ui";
import { plan, type Branding, type Projection } from "./lib/plan";
import { dataHora } from "./lib/format";
import {
  agruparMarcadores, estiloDoRastro, liveSocket, salvarAgrupamento, traccar,
  tamanhoDoRastro, Unauthorized, permissoesDe,
  type Device, type Evento, type Servidor, type User,
} from "./lib/traccar";
import { auth as autenticacao } from "./lib/auth";
import { aplicarFonte, aplicarMarca } from "./lib/marca";

const PAGINAS: Record<string, string> = {
  "/": "Painel",
  "/mapa": "Mapa ao vivo",
  "/relatorios": "Relatórios",
  "/cadastros": "Cadastros",
  "/conexao": "Conexão",
  "/personalizar": "Personalizar",
  "/auditoria": "Auditoria",
  "/logs": "Log do servidor",
  "/conta": "Minha conta",
  "/espaco": "Espaço e histórico",
  "/upgrade": "Meu servidor",
  "/faturamento": "Faturamento",
  "/minhas-faturas": "Minhas faturas",
  "/manual": "Manual",
  "/eventos": "Eventos",
};

export default function App() {
  const loc = useLocation();
  // Declarado aqui, e não lá embaixo: o efeito do `?token=` usa isto, e uma
  // `const` depois do efeito só funciona por acidente de ordem de execução.
  const navegar = useNavigate();
  const [marca, setMarca] = useState<Branding | null>(null);
  const [proj, setProj] = useState<Projection | null>(null);
  const [auth, setAuth] = useState<"?" | "sim" | "não">("?");
  const [usuario, setUsuario] = useState<User | null>(null);
  const [servidor, setServidor] = useState<Servidor | null>(null);
  // Dados que o topo consome: busca de veículo e sininho de alertas.
  const [devices, setDevices] = useState<Map<number, Device>>(new Map());
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  // O index.html já resolveu o tema antes da pintura; aqui só ecoamos.
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === "dark");
  const [telaPublica, setTelaPublica] = useState<"login" | "recuperar" | "registrar">(() => {
    const q = new URLSearchParams(window.location.search);
    return q.has("passwordReset") || window.location.pathname === "/reset-password"
      ? "recuperar" : "login";
  });
  const tokenReset = new URLSearchParams(window.location.search).get("passwordReset");

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    try { localStorage.setItem("theme", dark ? "dark" : "light"); } catch { /* privado */ }
  }, [dark]);

  useEffect(() => {
    plan.branding().then(b => {
      setMarca(b);
      document.title = b.name;          // white label: o título é do cliente
      aplicarMarca(b.cor);
      aplicarFonte(b.fonte);
    }).catch(() => setMarca({ slug: "", name: "Rastreamento", tracker_host: "" }));
  }, []);

  // A tela Personalizar avisa por evento quando a identidade muda.
  useEffect(() => {
    const aplicar = (e: Event) => {
      const b = (e as CustomEvent<Branding>).detail;
      setMarca(b);
      document.title = b.name;
      aplicarMarca(b.cor);
      aplicarFonte(b.fonte);
    };
    window.addEventListener("marca:atualizada", aplicar);
    return () => window.removeEventListener("marca:atualizada", aplicar);
  }, []);

  // Trocar a própria foto ou o próprio nome tem de aparecer no cabeçalho na
  // hora. A sessão é lida uma vez, na entrada; sem este aviso a mudança só
  // apareceria no próximo F5.
  useEffect(() => {
    const reler = () => traccar.session().then(setUsuario).catch(() => {});
    window.addEventListener("usuario:atualizado", reler);
    return () => window.removeEventListener("usuario:atualizado", reler);
  }, []);

  const checar = useCallback(() => {
    traccar.session()
      .then(u => { setUsuario(u); setAuth("sim"); })
      .catch(e => { setUsuario(null); setAuth(e instanceof Unauthorized ? "não" : "não"); });
  }, []);

  useEffect(() => {
    // Link compartilhado: `?token=…` entra direto, sem senha. O token sai da
    // URL depois de usado — barra de endereço é copiada e colada o tempo todo.
    const q = new URLSearchParams(window.location.search);
    const token = q.get("token");
    if (!token) { checar(); return; }
    autenticacao.entrarComToken(token)
      .then(u => {
        setUsuario(u); setAuth("sim");
        // ⚠️ O link existe para acompanhar um veículo: a tela certa é o MAPA.
        // Cair no Painel mostraria indicadores de uma frota que esta sessão
        // nem enxerga, e obrigaria quem recebeu o link a procurar o mapa num
        // menu que, aqui, não existe.
        if (window.location.pathname !== "/mapa") navegar("/mapa", { replace: true });
      })
      .catch(() => setAuth("não"))
      .finally(() => {
        q.delete("token");
        const resto = q.toString();
        window.history.replaceState({}, "",
          window.location.pathname + (resto ? `?${resto}` : ""));
      });
  }, [checar]);

  // ⚠️ `/api/server` traz as preferências da INSTALAÇÃO, e hoje só o tamanho
  // do rastro sai daqui. Falhar não pode travar nada: sem ele, o rastro usa o
  // padrão, que é o que aconteceria de qualquer jeito na maioria das contas.
  useEffect(() => {
    if (auth !== "sim") return;
    traccar.servidor().then(setServidor).catch(() => setServidor(null));
  }, [auth]);

  // A projeção só é buscada depois do login: antes disso não há o que mostrar.
  //
  // ⚠️ O recarregamento no evento não é refinamento. Sem ele, o cliente contrata
  // +10 GB, volta a ter os 45 dias, e a faixa continua avisando "Histórico
  // reduzido, 31 dias" por até cinco minutos — cobrando de novo por algo que ele
  // acabou de pagar.
  useEffect(() => {
    if (auth !== "sim" || usuario?.temporary) return;
    const buscar = () => plan.projection().then(setProj).catch(() => {});
    plan.projection().then(setProj).catch(() => setProj(null));
    const t = setInterval(buscar, 300000);
    window.addEventListener("espaco:atualizado", buscar);
    return () => { clearInterval(t); window.removeEventListener("espaco:atualizado", buscar); };
  }, [auth, usuario]);

  // Frota e alertas alimentam a busca e o sininho do topo em qualquer página.
  useEffect(() => {
    if (auth !== "sim") return;
    const carregar = () => {
      traccar.devices()
        .then(ds => setDevices(new Map(ds.map(d => [d.id, d]))))
        .catch(() => {});
      traccar.eventos(new Date(Date.now() - 86400000), new Date())
        .then(es => setEventos(es.sort(
          (a, b) => +new Date(b.eventTime) - +new Date(a.eventTime))))
        .catch(() => setEventos([]));
    };
    carregar();
    const t = setInterval(carregar, 120000);

    /**
     * Socket só de eventos, para o aviso na tela e o sininho.
     *
     * ⚠️ É um SEGUNDO socket: o mapa e o painel abrem o deles para posições,
     * por `useFrota`. O servidor guarda um conjunto de ouvintes por usuário
     * (`ConnectionManager.listeners`), então os dois recebem — e o aviso
     * precisa funcionar em qualquer página, não só nas que mostram mapa.
     */
    const socket = liveSocket({
      onEvents: es => {
        setEventos(prev => [...es, ...prev]
          .sort((a, b) => +new Date(b.eventTime) - +new Date(a.eventTime))
          .slice(0, 200));
        setAvisos(prev => [...prev, ...es.map(e => ({
          // Dois eventos do mesmo tipo no mesmo segundo são eventos diferentes;
          // a chave precisa disso ou o React funde os dois avisos num só.
          chave: `${e.id}-${e.eventTime}-${Math.random().toString(36).slice(2, 7)}`,
          evento: e,
          nome: "",
        }))]);
      },
    });

    return () => { clearInterval(t); socket.parar(); };
  }, [auth]);

  const pode = permissoesDe(usuario);

  /*
   * Agrupamento de marcadores.
   *
   * ⚠️ O estado é LOCAL e aplicado na hora; a gravação vem depois. O PUT leva
   * centenas de milissegundos, e esperar por ele faria o mapa só reagir bem
   * depois do clique — o botão pareceria emperrado.
   *
   * ⚠️ Se a gravação falhar, o botão VOLTA. Deixá-lo ligado prometeria uma
   * escolha que não sobrevive ao próximo login.
   */
  const [agrupar, setAgrupar] = useState<boolean | null>(null);
  const agrupado = agrupar ?? agruparMarcadores(usuario, servidor);

  async function alternarAgrupamento() {
    if (!usuario) return;
    const proximo = !agrupado;
    setAgrupar(proximo);
    try {
      await salvarAgrupamento(usuario, proximo);
      // Relê a sessão: é a mesma porta que o cadastro de usuário usa, e deixa
      // os atributos em um lugar só.
      window.dispatchEvent(new Event("usuario:atualizado"));
    } catch {
      setAgrupar(!proximo);
    }
  }

  const secoes: SecaoNav[] = useMemo(() => {
    const operacao = [
      { to: "/", label: "Painel", icon: LayoutDashboard },
      { to: "/mapa", label: "Mapa ao vivo", icon: MapIcon },
    ];
    // A tela junta os onze relatórios do painel (docs/15); chamar de "Histórico"
    // escondia dez deles atrás de um nome que promete só o trajeto de ontem.
    // ⚠️ `ui.disableEvents` é preferência de interface, não permissão: tira do
    // menu o que aquela conta não usa. Quem tem o endereço ainda alcança a
    // tela, e é o servidor que recusa o que não é permitido.
    if (pode.eventos) operacao.push({ to: "/eventos", label: "Eventos", icon: Siren });
    if (pode.relatorios) operacao.push({ to: "/percurso", label: "Percurso", icon: RotaIcon });
    if (pode.relatorios) operacao.push({ to: "/relatorios", label: "Relatórios", icon: FileBarChart });
    const conf = [];
    if (pode.cadastros) {
      conf.push({ to: "/cadastros", label: "Cadastros", icon: Settings });
      conf.push({ to: "/conexao", label: "Conexão", icon: Cable });
      conf.push({ to: "/personalizar", label: "Personalizar", icon: Palette });
    }
    // Auditoria e contadores do servidor são de quem administra, não de quem
    // dirige: os dois endpoints exigem administrador e devolveriam 403.
    // Faturamento dos clientes DO TENANT (ADR-0015). Mexe em credencial de
    // pagamento, então é de quem administra — e o endpoint recusa os outros.
    if (usuario?.administrator)
      conf.push({ to: "/faturamento", label: "Faturamento", icon: Receipt });
    // Vale para todo mundo: quem paga precisa achar o próprio boleto.
    operacao.push({ to: "/minhas-faturas", label: "Minhas faturas", icon: ReceiptText });
    if (usuario?.administrator) conf.push({ to: "/auditoria", label: "Auditoria", icon: ShieldCheck });
    // ⚠️ Só administrador: o log traz o quadro CRU de cada rastreador, com
    // identificador e coordenada, e NÃO passa pelas permissões de equipamento.
    // Quem o lê vê a frota inteira — inclusive a de quem não teria acesso.
    if (usuario?.administrator) conf.push({ to: "/logs", label: "Log do servidor", icon: ScrollText });
    if (pode.conta) conf.push({ to: "/conta", label: "Minha conta", icon: UserCog });
    // ⚠️ Configuração de rastreador manda comando CRU para equipamento em
    // campo: um `factory reset` no roteiro errado é visita técnica em cada
    // veículo. Só administrador — e os endpoints de roteiro recusam os outros,
    // porque esconder o menu não protege a API.
    if (usuario?.administrator)
      conf.push({ to: "/configurar", label: "Configurar rastreadores", icon: Terminal });
    // Por último de propósito: é o item que a pessoa procura quando travou,
    // e um menu que começa pela ajuda parece um sistema que espera confusão.
    operacao.push({ to: "/manual", label: "Manual", icon: BookOpen });

    // ⚠️ "Meu servidor" entra DEPOIS de tudo, e aqui e não no meio: ele é o
    // último item do menu por decisão, e "Configuração" é o último grupo —
    // então este é o último item que a pessoa vê. Empurrá-lo junto dos outros
    // deixaria a posição dependente da ordem dos `push`, e quem acrescentasse
    // um item adiante cairia atrás dele sem perceber.
    //
    // Recursos contratados e ampliação (ADR-0016). De quem administra: mexe no
    // que a empresa paga, e os blocos entram na fatura.
    //
    // A rota segue `/upgrade`: trocá-la quebraria o endereço que quem
    // administra já tenha guardado, e o nome da tela não depende dela.
    if (usuario?.administrator)
      conf.push({ to: "/upgrade", label: "Meu servidor", icon: TrendingUp });

    return conf.length
      ? [{ titulo: "Operação", itens: operacao }, { titulo: "Configuração", itens: conf }]
      : [{ titulo: "Operação", itens: operacao }];
  }, [pode.relatorios, pode.cadastros, pode.conta, usuario?.administrator]);

  if (auth === "?" || !marca) return <div className="flex min-h-full items-center"><Spinner /></div>;

  if (auth === "não") {
    const comum = { nome: marca.name, logo: marca.logo_url };
    if (telaPublica === "recuperar")
      return (
        <Suspense fallback={<Spinner />}>
          <Recuperar {...comum} token={tokenReset} aoVoltar={() => {
            setTelaPublica("login");
            if (tokenReset) window.history.replaceState({}, "", "/");
          }} />
        </Suspense>
      );
    if (telaPublica === "registrar")
      return (
        <Suspense fallback={<Spinner />}>
          <Registrar {...comum} aoVoltar={() => setTelaPublica("login")} />
        </Suspense>
      );
    return <Login {...comum} onEntrar={checar}
                  aoRecuperar={() => setTelaPublica("recuperar")}
                  aoRegistrar={() => setTelaPublica("registrar")} />;
  }

  const titulo = PAGINAS[loc.pathname] ?? "Painel";
  /*
   * Páginas que mandam no espaço todo: sem título, sem faixa e sem rolagem.
   *
   * ⚠️ O Percurso entrou aqui por um motivo medido: fora do modo cheio o
   * `main` não limita a altura, o `h-full` da página não resolve, e o mapa
   * crescia até 1.489 px numa janela de 950 — a rota ficava enquadrada no meio
   * dele, ou seja, abaixo da dobra, e a tela parecia não ter desenhado nada.
   */
  const cheio = loc.pathname === "/mapa" || loc.pathname === "/percurso";

  const faixa = pode.compartilhada ? (
    <p className="flex flex-wrap items-center gap-x-2 rounded-[var(--radius-card)] border
                  border-ink-200 bg-white px-4 py-3 text-sm text-ink-600
                  dark:border-ink-800 dark:bg-ink-900 dark:text-ink-300">
      <ShieldCheck size={15} style={{ color: "var(--color-brand-tenant)" }} aria-hidden />
      <span>
        Acompanhamento compartilhado
        {usuario?.name && <> de <strong>{usuario.name}</strong></>}
        {usuario?.expirationTime && <> · válido até {dataHora(usuario.expirationTime)}</>}
      </span>
    </p>
  ) : <QuotaBanner p={proj} />;

  return (
    <MotionConfig reducedMotion="user">
      <Avisos
        avisos={avisos.map(a => ({
          ...a, nome: devices.get(a.evento.deviceId)?.name ?? `Veículo ${a.evento.deviceId}`,
        }))}
        aoFechar={chave => setAvisos(prev => prev.filter(a => a.chave !== chave))} />

      <Shell secoes={secoes} semMenu={pode.compartilhada} marca={marca.name} logo={marca.logo_url}
             usuario={usuario} eventos={eventos} devices={devices}
             dark={dark} onTema={() => setDark(d => !d)} cheio={cheio}
             faixa={cheio ? undefined : faixa}
             onSair={() => traccar.logout().finally(() => setAuth("não"))}>
        {!cheio && <Titulo titulo={titulo} trilha={titulo} />}

        <AnimatePresence mode="wait">
          <motion.div key={loc.pathname} className={cheio ? "h-full" : undefined}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}>
            <Suspense fallback={<Spinner />}>
              <Routes location={loc}>
                <Route path="/" element={<Dashboard podeComandar={pode.comandos}
                                                    admin={!!usuario?.administrator} />} />
                <Route path="/mapa" element={
                  <Mapa podeComandar={pode.comandos}
                        podeCompartilhar={pode.compartilhar}
                        acoesVeiculo={pode.acoesVeiculo}
                        compartilhada={pode.compartilhada}
                        tamanhoRastro={tamanhoDoRastro(servidor, usuario)}
                        estiloRastro={estiloDoRastro(usuario)}
                        agrupar={agrupado}
                        aoAlternarAgrupar={usuario && !pode.compartilhada
                                           ? alternarAgrupamento : undefined} />} />
                {pode.relatorios && <Route path="/relatorios" element={<Relatorios />} />}
                {/* Mesma permissão dos relatórios: é o mesmo dado, desenhado. */}
                {pode.relatorios && <Route path="/percurso" element={<Percurso />} />}
                {/* Quem tem o endereço antigo guardado não cai em tela branca. */}
                <Route path="/historico" element={<Navigate to="/relatorios" replace />} />
                {pode.cadastros && <Route path="/cadastros" element={<Cadastros />} />}
                {pode.cadastros && <Route path="/conexao" element={<Conexao />} />}
                {pode.cadastros && <Route path="/personalizar" element={<Personalizar />} />}
                {/* Faturamento mexe em credencial de pagamento: só administrador. */}
                {usuario?.administrator && <Route path="/faturamento" element={<Faturamento />} />}
                {usuario?.administrator && <Route path="/configurar" element={<Configurar />} />}
                {/* Do cliente final, que nunca é administrador. O servidor
                    filtra pelo cookie, então não há id na rota. */}
                <Route path="/minhas-faturas" element={<MinhasFaturas />} />
                {usuario?.administrator && <Route path="/auditoria" element={<Auditoria />} />}
                {usuario?.administrator && <Route path="/logs" element={<Logs />} />}
                {pode.conta && <Route path="/conta" element={<Conta marca={marca.name} />} />}
                {/* Fora do menu de propósito: a promessa vendida é "dispositivos
                    ilimitados", e um item fixo chamado "Espaço" transforma a
                    promessa em medidor. Quem chega aqui veio pela faixa de aviso
                    — ou digitou o endereço, e aí a resposta não pode ser 404. */}
                <Route path="/espaco" element={<Espaco />} />
                {/* ⚠️ No menu, ao contrário do /espaco acima. A razão daquele
                    comentário era o modelo ANTIGO: vendia-se "dispositivos
                    ilimitados" e um medidor fixo transformava a promessa em
                    limite. Com o ADR-0016 a infraestrutura É o produto, então
                    mostrá-la é o produto — não a letra miúda dele. */}
                <Route path="/upgrade" element={<Upgrade />} />
                {/* Vale para todo mundo: quem acabou de receber o acesso é
                    justamente quem mais precisa do manual. */}
                {/* O manual esconde as aulas de tela que só administrador abre:
                    ensinar um caminho que termina em "acesso negado" é pior
                    que não ensinar. Ver `somente_admin` no `roteiros.py`. */}
                <Route path="/manual" element={<Manual admin={!!usuario?.administrator} />} />
                <Route path="/eventos" element={<Eventos />} />
                {/* Caminho que a sessão não alcança volta ao painel, em vez de
                    mostrar uma tela em branco. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </Shell>
    </MotionConfig>
  );
}
